import {App} from "@vue/runtime-core";
import {createApp} from "vue";
import PeppermillCalendar from "peppermill-vue/src/components/PeppermillCalendar.vue";
import {getDateFromResNetInput} from "./calendar-helpers";

declare global {
  interface Window {
    pepDateFunctions: {
      language: {
        en: {
          days: string[];
          daysShort: string[];
          daysMin: string[];
          months: string[];
          monthsShort: string[];
          today: string;
          clear: string;
          dateFormat: string;
          timeFormat: string;
          firstDay: Number;
        }
      }
      formatDateObjectYYYYMMDD: (dateObject: Date) => string;
      daysBetween: (startDate: Date, endDate: Date) => number;
      addDays: (dateObject: Date, days: number) => Date;
      dateSuffix: (dayOfMonth: number) => string;
    };
    calendarApp?: App;
    promptUserToRefreshRates: (timerDelay?: number, message?: string) => void;
    // @ts-ignore
    resnetSaveReservationSelections: (checkInDate: Date, checkOutDate: Date, overrides?: {}) => void;
  }
}

function getGivenDateOrToday(givenDate: string): string {
  const todayString = formatDateObjectYYYYMMDD(new Date());
  const todaySortString = todayString.replace(/[^0-9]/g, '');
  const givenSortString = givenDate.replace(/[^0-9]/g, '');

  if (givenSortString < todaySortString) {
    return todayString;
  }

  return givenDate;
}

function formatDateObjectYYYYMMDD(date: Date): string {
  const todayDateString = date.toLocaleDateString('en-US', {timeZone: 'America/Los_Angeles'});
  const todayDateParts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(todayDateString);
  if (todayDateParts) {
    if (10 > parseInt(todayDateParts[1], 10)) {
      todayDateParts[1] = '0' + todayDateParts[1];
    }
    if (10 > parseInt(todayDateParts[2], 10)) {
      todayDateParts[2] = '0' + todayDateParts[2];
    }
    if (100 > parseInt(todayDateParts[3], 10)) {
      todayDateParts[3] = '20' + todayDateParts[3];
    }

    return todayDateParts[3] + '-' + todayDateParts[1] + '-' + todayDateParts[2];
  }

  return window.pepDateFunctions.formatDateObjectYYYYMMDD(date);
}

function getDateFromYYYYMMDD(date: string): Date | null {
  const dateParts = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(date);
  if (dateParts) {
    return new Date(parseInt(dateParts[1], 10), parseInt(dateParts[2], 10) - 1, parseInt(dateParts[3], 10));
  }

  return null;
}

const offerCode = (document.querySelector('input[name="AOFFERRO  "]') as HTMLInputElement || {value: ''}).value;
const partition = (document.querySelector('input[name="_PARTITION"]') as HTMLInputElement || {value: ''}).value;
const query = 'ACTION=GET_SINGLE_RESNET_OFFER_DATA&OFFER_CODE=' + encodeURIComponent(offerCode) + '&PARTITION=' + encodeURIComponent(partition) + '&PROPERTY_ID=' + encodeURIComponent(partition.substring(0, 2));
const apiUrl = (location.hostname === 'reserve1.peppermillcas.com' || location.hostname === 'booking.wendoverfun.com')
  ? 'https://reserve1.peppermillcas.com:4323/resnet-api/'
  : 'https://chili.peppermillcas.com:4323/resnet-api/';
fetch(
  apiUrl + '?' + query,
  {
    method: 'GET',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    },
    redirect: 'follow'
  })
  .then(response => {
    return response.json();
  })
  .then(json => {
    const componentProps: { [key: string]: string | number } = {
      hotelPhoneNumber: '800-537-0207'
    };
    if ('STATUS' in json && 'SUCCESS' === json.STATUS && 'DATA' in json && 'object' === typeof json.DATA) {
      if ('START_DATE' in json.DATA) {
        componentProps['start'] = getGivenDateOrToday(json.DATA.START_DATE);
      }
      if ('END_DATE' in json.DATA) {
        componentProps['end'] = json.DATA.END_DATE;
      }
      if ('MIN_NIGHTS' in json.DATA) {
        componentProps['minDays'] = json.DATA.MIN_NIGHTS;
      }
      if ('MAX_NIGHTS' in json.DATA) {
        componentProps['maxDays'] = json.DATA.MAX_NIGHTS;
      }
    }

    const checkInDateString = (document.querySelector('input[name="PSDW1GM   "]') as HTMLInputElement || {value: ''}).value;
    const checkOutDateString = (document.querySelector('input[name="PEDW1GM   "]') as HTMLInputElement || {value: ''}).value;
    if (checkInDateString.length && checkOutDateString.length) {
      const checkOutDate = getDateFromResNetInput(checkOutDateString);
      if (checkOutDate) {
        componentProps['initialSelectionEndDate'] = checkOutDate;
      }

      const checkInDate = getDateFromResNetInput(checkInDateString);
      if (checkInDate) {
        const dateChecked = getGivenDateOrToday(checkInDate);
        componentProps['initialSelectionStartDate'] = dateChecked;

        //update check out date if check in date was changed
        if (dateChecked !== checkInDate) {
          if (checkOutDate) {
            const newCheckOutDate = window.pepDateFunctions.addDays(
              //@ts-ignore
              getDateFromYYYYMMDD(checkOutDate),
              window.pepDateFunctions.daysBetween(
                //@ts-ignore
                getDateFromYYYYMMDD(checkInDate),
                getDateFromYYYYMMDD(dateChecked)
              )
            );
            componentProps['initialSelectionEndDate'] = formatDateObjectYYYYMMDD(newCheckOutDate);
          }
        }
      }
    }

    const rootNode = document.querySelector('#calendar-widget');
    if (rootNode) {
      const app = createApp(PeppermillCalendar, componentProps);

      app.mount(rootNode);

      window.calendarApp = app;

      //add "close" button to calendar widget
      const closeButtonNode = document.createElement('A') as HTMLAnchorElement;
      closeButtonNode.innerText = 'Close';
      closeButtonNode.href = '#';
      closeButtonNode.style.padding = '0 0.75rem 0.75rem 0';
      closeButtonNode.style.float = 'right';
      closeButtonNode.addEventListener('click', event => {
        event.preventDefault();
        rootNode.classList.remove('calendar_widget-open');
      });

      rootNode.appendChild(closeButtonNode);

      const calendarWrapper = rootNode.closest('.pepvalidate-v3') as HTMLElement | null;
      if (calendarWrapper) {
        calendarWrapper.style.transition = 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 350ms';
        calendarWrapper.classList.remove('pepvalidate-v3-hidden');
      }
    }
  });

// @ts-ignore
require('./calendar');